import { Controller } from "@hotwired/stimulus"
import { announcePopoverOpen, bindExclusivePopover } from "../utils/exclusive-popover.util"
import { isCompactViewport } from "../utils/pointer.util"
import { cancelPopoverClose, closeWithPopoverMotion } from "../utils/popover-motion.util"

const OPENING_CLASS = "is-popover-opening"

export default class extends Controller {
  connect() {
    this.unbindExclusivePopover = bindExclusivePopover(this.element, () => this.close())
    this.boundHandleSummaryClick = this.#handleSummaryClick.bind(this)
    this.boundHandleMenuAnimationEnd = this.#handleMenuAnimationEnd.bind(this)
    this.boundWindowResize = this.#positionMenu.bind(this)
    this.summaryElement?.addEventListener("click", this.boundHandleSummaryClick)
    window.addEventListener("resize", this.boundWindowResize, { passive: true })
  }

  disconnect() {
    this.unbindExclusivePopover?.()
    this.summaryElement?.removeEventListener("click", this.boundHandleSummaryClick)
    this.menuElement?.removeEventListener("animationend", this.boundHandleMenuAnimationEnd)
    window.removeEventListener("resize", this.boundWindowResize)
    this.#clearMenuPosition()
  }

  sync() {
    if (this.element.open) {
      cancelPopoverClose(this.menuElement)
      this.#positionMenu()
      this.#restartOpeningMotion()
      announcePopoverOpen(this.element)
    }
  }

  close() {
    if (!this.element.open) return

    this.menuElement?.classList.remove(OPENING_CLASS)
    closeWithPopoverMotion(this.menuElement, () => {
      this.element.open = false
      this.#clearMenuPosition()
    })
  }

  #handleSummaryClick(event) {
    if (!this.element.open) return

    event.preventDefault()
    this.close()
  }

  #restartOpeningMotion() {
    const menu = this.menuElement
    if (!menu) return

    menu.removeEventListener("animationend", this.boundHandleMenuAnimationEnd)
    menu.classList.remove(OPENING_CLASS)
    void menu.offsetWidth
    menu.classList.add(OPENING_CLASS)
    menu.addEventListener("animationend", this.boundHandleMenuAnimationEnd)
  }

  #handleMenuAnimationEnd(event) {
    if (event.target !== this.menuElement) return

    this.menuElement?.classList.remove(OPENING_CLASS)
    this.menuElement?.removeEventListener("animationend", this.boundHandleMenuAnimationEnd)
  }

  #positionMenu() {
    const menu = this.menuElement
    if (!menu || !this.element.open) return

    if (!isCompactViewport()) {
      this.#clearMenuPosition()
      return
    }

    const spacing = 8
    const summaryRect = this.summaryElement.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const top = Math.max(spacing, Math.floor(summaryRect.bottom + spacing))

    menu.style.position = "fixed"
    menu.style.top = `${top}px`
    menu.style.left = `${spacing}px`
    menu.style.right = `${spacing}px`
    menu.style.width = "auto"
    menu.style.maxHeight = `${Math.max(220, viewportHeight - top - spacing)}px`
    menu.style.overflowY = "auto"
    menu.style.zIndex = "1004"
  }

  #clearMenuPosition() {
    const menu = this.menuElement
    if (!menu) return

    menu.style.removeProperty("position")
    menu.style.removeProperty("top")
    menu.style.removeProperty("left")
    menu.style.removeProperty("right")
    menu.style.removeProperty("width")
    menu.style.removeProperty("max-height")
    menu.style.removeProperty("overflow-y")
    menu.style.removeProperty("z-index")
  }

  get summaryElement() {
    return this.element.querySelector("summary")
  }

  get menuElement() {
    return this.element.querySelector(".lc-table-sort__menu")
  }
}
